/**
 * The typed façade over the control plane (spec §13).
 *
 * Routes take a `MadalineApi` from the router context rather than importing a
 * module singleton, so a test drives the whole app with a stub and no network.
 * Every shape here is what `apps/api` actually returns today — the response
 * bodies were read out of `apps/api/src/plugins/{auth,strategies,runs,events}.ts`,
 * not invented from the spec prose.
 */
import type { Diagnostic, VenueT } from "@madaline/shared";
import type {
  ApiDefinitionT,
  ApiFieldTypeT,
  ApiVariableT,
  FieldHealthT,
} from "@madaline/shared/apis";
import type { RunEventT } from "@madaline/shared/events";
import { ApiError, createApiClient, type ApiClient } from "./api-client.js";
import { openSse, type OpenSseOptions, type SseHandle } from "./sse.js";
import {
  changeAccountPassword,
  listAuthSessions,
  revokeAuthSession,
  revokeOtherAuthSessions,
  signInWithPassword,
  signOutEverywhere,
  signOutHere,
  signUpWithPassword,
  type AuthSession,
  type PasswordCredentials,
  type SignInChallenge,
  type SignUpInput,
} from "./auth-client.js";

export type { AuthSession, SignInChallenge };

/**
 * The Phase 4 screens were built prop-driven, and each one's props are exactly
 * the row `apps/api` returns. Importing those interfaces back — type-only, so
 * nothing is pulled into the bundle — keeps one definition per wire shape
 * instead of a copy here that drifts from the component it feeds.
 */
import type { CredentialCardValue } from "../features/apis/CredentialCard.js";
import type { DisclosureStatusRow } from "../features/legal/DisclosuresGate.js";
import type { PairedAgent, PairingCode } from "../features/settings/Execution.js";
import type { PlanValue } from "../features/settings/Plan.js";
import type { RiskLimitsPatch, RiskLimitsValue } from "../features/settings/RiskLimits.js";

export type {
  CredentialCardValue,
  DisclosureStatusRow,
  PairedAgent,
  PairingCode,
  PlanValue,
  RiskLimitsPatch,
  RiskLimitsValue,
};

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/** §12.1: region | venue | execution | disclosures | first_strategy | done. */
export type OnboardingState =
  | "region"
  | "venue"
  | "execution"
  | "disclosures"
  | "first_strategy"
  | "done";

export interface MeProfile {
  display_name: string | null;
  preferred_venue: VenueT | null;
  onboarding_state: OnboardingState;
  country: string | null;
  region: string | null;
  is_us_person: boolean;
  polymarket_live_allowed: boolean;
  execution_locality_default: "hosted" | "self_hosted";
  timezone: string;
  plan: string;
  two_factor_enrolled: boolean;
}

export interface Me {
  user: { id: string; email: string; name: string | null };
  profile: MeProfile;
  disclosures: unknown[];
  risk_defaults: unknown;
  /**
   * Whether THIS server mounts `POST /me/2fa/dev-enable`, the development-only
   * step-up bypass. The route is not registered when the API's
   * `MADALINE_DEV_STEP_UP` flag is off — it answers a plain 404 — so this field
   * is the only way the browser can know, and Settings → Security shows its
   * bypass button on this and nothing else. It is false in every production
   * build: the API refuses to boot with that flag set and `NODE_ENV=production`.
   */
  dev_step_up_available: boolean;
}

export interface ActiveRunRef {
  id: string;
  mode: "paper" | "live";
  actual_state: string;
  desired_state?: string;
}

export interface StrategySummary {
  id: string;
  venue: VenueT;
  name: string;
  updated_at: string;
  ast_hash: string;
  document_version: number;
  has_errors: boolean;
  active_run: ActiveRunRef | null;
}

export interface StrategyDocument {
  id: string;
  venue: VenueT;
  name: string;
  description: string | null;
  workspace: unknown;
  ast: unknown;
  ast_hash: string;
  document_version: number;
  version_no: number;
  diagnostics: Diagnostic[];
  has_errors: boolean;
  models: unknown[];
  apis: unknown[];
  created_at: string;
  updated_at: string;
  active_run: ActiveRunRef | null;
}

export interface SaveResult {
  document_version: number;
  ast_hash: string;
  version_no: number;
  saved_at: string;
}

export interface PaperWalletSummary {
  id: string;
  currency: string;
  cash: string;
  starting_cash: string;
  realized_pnl: string;
  fees_paid: string;
  equity_mark: string | null;
}

export interface RunSummary {
  id: string;
  strategy_id: string;
  mode: "paper" | "live";
  venue: VenueT;
  venue_env: "prod" | "demo";
  locality: "hosted" | "self_hosted";
  desired_state: string;
  actual_state: string;
  stop_reason: string | null;
  last_iteration: number;
  started_at: string | null;
  stopped_at: string | null;
  checkpoint_at: string | null;
  last_error: unknown;
  risk_limit_snapshot: unknown;
  credential_id: string | null;
  ast_hash: string;
  lease_epoch: number;
  assigned_node_id: string | null;
  paper_wallet: PaperWalletSummary | null;
}

export interface RunHistoryPage {
  /** §13: "summary" means the Valkey stream expired and this is the mirror. */
  source: "stream" | "summary";
  complete: boolean;
  events: RunEventT[];
  next: string | null;
}

export interface StartRunInput {
  mode: "paper" | "live";
  executor?: "hosted" | { agent_id: string };
  venue_env?: "prod" | "demo";
  paper_cash?: string;
  confirm_live?: true;
}

/**
 * The only starting document Madaline will build for a user.
 *
 * Madaline is a platform, not a commodity trading advisor: supplying a ready-made
 * strategy — a loop, a market read, a trade with a price and a size — would be
 * recommending one (spec §2; CFTC Reg 4.14(a)(9)). The API refuses anything
 * else with 422 `TEMPLATE_NOT_AVAILABLE`; this type makes asking a type error,
 * so the constraint is caught at compile time rather than at runtime.
 */
export type UserCreatableTemplate = "blank";

export interface CreateStrategyInput {
  venue: VenueT;
  name?: string;
  template?: UserCreatableTemplate;
}

export interface SaveStrategyInput {
  workspace: unknown;
  ast: unknown;
  diagnostics?: Diagnostic[];
  base_document_version: number;
  force?: boolean;
}

export interface OnboardingInput {
  venue: VenueT;
  template: UserCreatableTemplate;
}

/**
 * `GET /me/risk-defaults` and `PUT /me/risk-defaults` (§12.2, §13).
 *
 * `effective` carries two fields beyond the nine the form edits — the market
 * pattern allow-list and Kalshi's order-group `contracts_limit` — which the
 * form leaves untouched and passes through by not sending them.
 *
 * `kalshi_order_group_contracts_limit` is NOT an exposure ceiling and must
 * never be presented as one (KNOWN-DEBT **D3**, measured): a Kalshi order
 * group counts FILLED contracts in a rolling 15-second window, so a group
 * limited to one contract still accepts a second RESTING one. It is a
 * venue-side dead-man switch — trip it and Kalshi cancels everything in the
 * group and refuses the next order — and the ceilings on how much a user can
 * hold are `max_position_per_market`, `max_order_qty` and `max_order_notional`,
 * which Madaline enforces itself.
 */
export interface EffectiveRiskLimits extends RiskLimitsValue {
  allowed_market_patterns: string[] | null;
  kalshi_order_group_contracts_limit: string;
}

export interface RiskDefaults {
  effective: EffectiveRiskLimits;
  sources: { user_default: unknown };
  /** Set while the daily-loss halt is in force (§12.2). */
  halted_until?: string | null;
  halt_reason?: string | null;
}

/** `GET /disclosures` (§7.10's gate, read as a list). */
export interface DisclosureStatus {
  ok: boolean;
  outstanding: string[];
  documents: DisclosureStatusRow[];
}

/**
 * `GET /me/2fa`. Never carries a secret, a QR payload or a recovery code —
 * those exist only in the one response that mints them.
 *
 * `enrolled` means VERIFIED. `pending` is the state between starting enrolment
 * and proving the first code, so the screen can say "finish this" rather than
 * "not enrolled" and send the user round the loop again.
 *
 * Everything below `pending` is optional because an older API answered this
 * route with two fields and nothing else; an absent `enrolment_available`
 * reads as false, which is exactly right for a server whose enrolment route is
 * still the 501 stub.
 */
export interface TwoFactorStatus {
  enrolled: boolean;
  pending?: boolean;
  /** Whether THIS server can actually enrol. The screen prefers it when true. */
  enrolment_available?: boolean;
  recovery_codes_remaining?: number;
  locked_until?: string | null;
  issuer?: string;
  digits?: number;
  period_seconds?: number;
  algorithm?: string;
  step_up_max_age_ms: number;
}

/**
 * `POST /me/2fa/enroll` — shown ONCE. `provisioning_uri` is the `otpauth://`
 * string an authenticator imports and `qr_payload` is the same text, to be
 * encoded into a QR in the browser: the server deliberately does not render
 * the image, so the secret never passes through an encoder or a log there.
 *
 * `secret` is the base32 form for manual entry. Nothing here is ever fetched
 * again — a lost recovery code is regenerated, not re-read.
 */
export interface TwoFactorEnrolment {
  provisioning_uri: string;
  qr_payload: string;
  secret: string;
  issuer: string;
  account_name: string;
  algorithm: string;
  digits: number;
  period_seconds: number;
  recovery_codes: string[];
  verified: false;
  next: string;
}

/**
 * `POST /me/2fa/verify` — the challenge. On success the CALLING session is
 * fresh until `expires_at`, after which the step-up routes refuse again.
 */
export interface TwoFactorChallengeResult {
  two_factor_enrolled: boolean;
  verified: boolean;
  fresh: boolean;
  fresh_at: string | null;
  expires_at: string | null;
  step_up_max_age_ms: number;
  /** True on the challenge that turned a pending enrolment into a real one. */
  enrolment_completed: boolean;
  recovery_code_used: boolean;
}

/**
 * `POST /me/2fa/dev-enable` — the development-only step-up bypass, mounted
 * only when `Me.dev_step_up_available` is true.
 *
 * It skips real TOTP enrolment entirely: it writes the two rows §13's guard
 * reads (`auth.two_factor`, `auth.session.fresh_at`) for the CALLING session
 * so step-up-gated actions can be exercised locally. `expires_at` is when the
 * freshness lapses and those actions go back to `401 STEP_UP_REQUIRED`.
 */
export interface DevStepUpResult {
  two_factor_enrolled: boolean;
  fresh: boolean;
  fresh_at: string | null;
  expires_at: string | null;
  step_up_max_age_ms: number;
  development_only: true;
}

// ---------------------------------------------------------------------------
// Custom APIs (§9.2, §9.3, §12.6)
// ---------------------------------------------------------------------------

/**
 * The definition half of every APIS response is the §9.2 `ApiDefinition`
 * **verbatim** — the same object the shared evaluator, the fetcher and the
 * block generator take — so the browser never reshapes it. Only the row
 * metadata around it (`last_test_at`, `created_at`, …) is snake_case, like
 * every other route in this app.
 */
export interface ApiDefinitionDetail
  extends Omit<
    ApiDefinitionT,
    "strategy_id" | "builtin_template_id" | "builtin_template_version" | "sampleResponse"
  > {
  strategy_id: string | null;
  builtin_template_id: string | null;
  builtin_template_version: number | null;
  sampleResponse: unknown;
  sample_captured_at: string | null;
  sample_status: number | null;
  last_test_at: string | null;
  last_test_status: number | null;
  last_test_duration_ms: number | null;
  created_at: string;
  updated_at: string;
}

/** One card in §14.5's "Custom APIs" list. */
export interface ApiDefinitionSummary {
  id: string;
  name: string;
  slug: string;
  scope: "global" | "strategy";
  strategy_id: string | null;
  kind: "custom" | "builtin";
  builtin_template_id: string | null;
  version: number;
  method: string;
  url: string;
  host: string;
  field_count: number;
  /** Enough for §9.6 to generate this definition's palette from the list row. */
  fields: { id: string; name: string; type: ApiFieldTypeT; multi: boolean }[];
  variables: ApiVariableT[];
  poll_interval_ms: number;
  last_test_at: string | null;
  last_test_status: number | null;
  broken_field_count: number;
  updated_at: string;
}

/** One row of the FIELDS table after a test run (§9.3 `fields_preview[]`). */
export interface ApiFieldPreview {
  id: string;
  name: string;
  path: string;
  type: ApiFieldTypeT;
  multi: boolean;
  health: FieldHealthT;
  value: unknown;
  error?: string;
  message?: string;
}

export interface ApiBrokenField {
  id: string;
  name: string;
  path: string;
  health: FieldHealthT;
  message?: string;
}

/** `POST /apis/:id/test` — the request really was made, server-side. */
export interface ApiTestResult {
  status: number;
  duration_ms: number;
  /** Already redacted by the server: a key reads `[redacted]`. */
  headers: Record<string, string>;
  body_preview: string;
  truncated: boolean;
  raw_truncated: boolean;
  url: string;
  method: string;
  cached: boolean;
  version: number;
  sample_captured_at: string | null;
  fields_preview: ApiFieldPreview[];
  field_health: Record<string, FieldHealthT>;
  broken_fields: ApiBrokenField[];
}

/**
 * `POST /apis/:id/preview-field` — what one path extracts from the stored
 * sample, computed by the same evaluator the strategy runtime uses. Nothing in
 * this bundle evaluates a JSONPath; this is the only source of a preview.
 */
export interface ApiPathPreview {
  path: string;
  type: ApiFieldTypeT;
  multi: boolean;
  value: unknown;
  health: FieldHealthT;
  error?: string;
  message?: string;
  /** How many nodes the path selected. */
  matches: number;
  /** Their normalized paths, for the "list of …" affordance. */
  paths: string[];
}

export interface CreateApiInput {
  scope: "global" | "strategy";
  strategy_id?: string;
  name?: string;
  slug?: string;
  request?: unknown;
  auth?: unknown;
  variables?: unknown;
  policy?: unknown;
  fields?: unknown;
  builtin_template_id?: string;
}

export type UpdateApiInput = Omit<CreateApiInput, "scope" | "strategy_id" | "builtin_template_id">;

/** An API that signs in with a secret, as `GET /secrets` names it. */
export interface ApiSecretUser {
  id: string;
  name: string;
}

/**
 * §13 `GET /secrets` — one saved secret, by NAME. No route ever returns its
 * value, its last four characters or its HMAC; `updated_at` is when its value
 * last changed (the date a self-hosted computer is told, D37).
 */
export interface ApiSecret {
  id: string;
  name: string;
  kind: "api_key" | "fred" | "bls" | "generic";
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  used_by: ApiSecretUser[];
}

export interface ApiSecretList {
  secrets: ApiSecret[];
  /** Names an API signs in with that nothing has saved. */
  missing: { name: string; used_by: ApiSecretUser[] }[];
  /** `false` on a deployment with no key to seal a secret with. */
  can_store: boolean;
}

/** `POST /secrets` (step-up) — set or replace one value. The answer never carries it. */
export interface SaveSecretResult {
  secret: ApiSecret;
  replaced: boolean;
}

/** §13 `POST /credentials/kalshi` (step-up). The PEM goes in once and never comes back. */
export interface ConnectKalshiKeyInput {
  env: "prod" | "demo";
  key_id: string;
  pem: string;
  label?: string;
  acknowledge_transfer?: boolean;
}

export type ConnectedKalshiKey = CredentialCardValue & {
  credential_id: string;
  last4: string;
  requires_acknowledgment: boolean;
};

export interface ApiUsedBy {
  id: string;
  name: string;
  parser_version: number | null;
  stale: boolean;
}

// ---------------------------------------------------------------------------
// Datasets and models (§13 "Datasets & models", §10, §12.7)
// ---------------------------------------------------------------------------

/** One row of `GET /datasets/coverage` — what a VENUE was measured to have. */
export interface VenueCoverageEntry {
  source: string;
  venue: string;
  series: string;
  kind: string;
  label: string;
  from_ts: number | null;
  to_ts: number | null;
  rows: number | null;
  note: string;
  depth: "measured" | "unmeasured" | "none";
  bar_minutes: number | null;
  market_minutes: number | null;
  measured_at: string | null;
  continuing: boolean;
  endpoint: string;
  evidence: string;
}

export interface VenueCoverageResponse {
  coverage: VenueCoverageEntry[];
  measured_at: string | null;
  note: string;
}

export interface DatasetSourceRef {
  id: string;
  source_kind: string;
  executor: string;
  source: string | null;
  requested: { from_ts: number | null; to_ts: number | null };
  status: string;
  last_error: string | null;
  last_fetched_ts: number | null;
  bull_job_id: string | null;
}

export interface DatasetSummary {
  id: string;
  name: string;
  scope: "global" | "strategy";
  strategy_id: string | null;
  venue: string | null;
  description: string | null;
  columns: string[];
  row_count: number;
  bytes: number;
  earliest_ts: number | null;
  latest_ts: number | null;
  status: string;
  content_hash: string | null;
  retention_days: number | null;
  created_at: number | null;
  updated_at: number | null;
  sources: DatasetSourceRef[];
}

/** One `(venue, series, kind)` triple's REAL extent inside a dataset. */
export interface DatasetSourceCoverage {
  venue: string;
  series: string;
  kind: string;
  rows: number;
  from_ts: number;
  to_ts: number;
  interval_ms: number | null;
  interval_source: "declared" | "inferred" | "unknown";
  gaps: { from_ts: number; to_ts: number; missing: number }[];
  gap_count: number;
  gaps_truncated: boolean;
  /** `null` means no cadence is known — the question has no answer, not zero. */
  missing_rows: number | null;
  complete: boolean | null;
}

export interface DatasetCoverageResponse {
  dataset_id: string;
  coverage: {
    dataset_id: string;
    rows: number;
    bytes: number;
    from_ts: number | null;
    to_ts: number | null;
    columns: string[];
    sources: DatasetSourceCoverage[];
  };
  requested: {
    source_id: string;
    source: string | null;
    from_ts: number | null;
    to_ts: number | null;
    status: string;
  }[];
  note: string;
}

export interface CreateDatasetInput {
  name: string;
  scope: "global" | "strategy";
  strategy_id?: string;
  description?: string;
  sources: { source: string; from_ts: number; to_ts: number; credential_id?: string }[];
}

export interface CreateDatasetResult {
  dataset_id: string;
  job_ids: string[];
  agent_jobs: unknown[];
  queued?: boolean;
  message?: string;
}

/** §12.7 `model_versions.status`. */
export type ModelVersionStatus =
  | "pending"
  | "training"
  | "ready"
  | "failed"
  | "cancelled"
  | "deprecated";

/** One raw input of a version's `feature_spec`, as §5.2.7 needs it for slots. */
export interface ModelRawInput {
  name: string;
  kind: "series" | "scalar";
  source: string;
  provider?: string;
  symbol?: string;
  bar_minutes?: number;
  lookback?: number;
  primary?: boolean;
  dtype?: string;
}

export interface ModelVersionSummary {
  version_no: number;
  status: ModelVersionStatus;
  /** Bare lowercase hex, 64 chars, NO `sha256:` prefix (§10.3). */
  feature_spec_hash: string | null;
  is_active: boolean;
  raw_inputs: ModelRawInput[];
  /** A handful of headline numbers for the card. Every one may be `null`. */
  metrics_summary: Record<string, number | null> | null;
  used_by: number;
  created_at: number | null;
  ready_at: number | null;
  /** Present while `status === 'training'`. */
  progress?: TrainingProgress | null;
  job_id?: string | null;
  error?: { class?: string; message?: string } | null;
}

export interface ModelSummary {
  id: string;
  name: string;
  scope: "global" | "strategy";
  strategy_id: string | null;
  family: string;
  task: string;
  description: string | null;
  dataset_id: string | null;
  active_version_no: number | null;
  latest_version_no: number;
  /** The wizard draft (`PUT /models/:id/draft`). */
  spec: unknown;
  used_by: number;
  created_at: number | null;
  updated_at: number | null;
  versions: ModelVersionSummary[];
}

export interface ModelList {
  global: ModelSummary[];
  local: ModelSummary[];
}

export interface CreateModelInput {
  name: string;
  scope: "global" | "strategy";
  strategy_id?: string;
  family: string;
  task: string;
  description?: string;
}

/** The §10.5 metrics for one segment. Every number may be `null`. */
export interface SegmentMetrics {
  accuracy?: number | null;
  balanced_accuracy?: number | null;
  auc?: number | null;
  auc_per_class?: (number | null)[];
  log_loss?: number | null;
  brier?: number | null;
  precision?: number | null;
  recall?: number | null;
  positive_rate?: number | null;
  threshold?: number;
  confusion?: number[][];
  confusion_labels?: number[];
  class_recall?: (number | null)[];
  class_support?: number[];
  calibration_bins?: {
    lo: number;
    hi: number;
    n: number;
    mean_predicted: number | null;
    observed: number | null;
  }[];
  mae?: number | null;
  rmse?: number | null;
  r2?: number | null;
  directional_accuracy?: number | null;
  mean_prediction?: number | null;
  mean_actual?: number | null;
  equity?: EquitySummary | null;
}

export interface EquitySummary {
  n: number;
  n_trades?: number;
  exposure?: number | null;
  total_return?: number | null;
  gross_return?: number | null;
  cost_drag?: number | null;
  mean_bar_return?: number | null;
  volatility?: number | null;
  information_ratio?: number | null;
  max_drawdown?: number | null;
  hit_rate?: number | null;
  haircut_bps: number;
  position_rule: string;
  overlapping?: boolean;
}

export interface ModelMetrics {
  segment_sizes: Record<string, number>;
  threshold: number;
  haircut_bps: number;
  calibration: string;
  train?: SegmentMetrics;
  val?: SegmentMetrics;
  test?: SegmentMetrics;
}

/** §10.5's down-sampled `{t, y, p, fwd_ret}` — the client recomputes from these. */
export interface TestPrediction {
  t: number;
  y: number | null;
  p: number | null;
  fwd_ret: number | null;
}

export interface ModelVersionDetail extends ModelVersionSummary {
  model_id: string;
  task: "binary" | "multiclass" | "regression";
  family: string;
  feature_spec: unknown;
  target_spec: unknown;
  split_spec: unknown;
  calibration: string;
  metrics: ModelMetrics | null;
  manifest: unknown;
  train_config: unknown;
  /**
   * The rows a version was trained on. The trainer writes `first_ts` /
   * `last_ts` (and per-segment ranges); `from_ts` / `to_ts` are the older
   * names. Either may be absent.
   */
  data_range: {
    from_ts?: number | null;
    to_ts?: number | null;
    first_ts?: number | null;
    last_ts?: number | null;
    rows?: number;
  } | null;
  dataset_id: string | null;
  dataset_content_hash: string | null;
  artifact_bytes: number | null;
  file_hashes: Record<string, string> | null;
  test_predictions_url: string | null;
  warnings?: string[];
}

export interface CreateVersionInput {
  dataset_id: string;
  feature_spec: unknown;
  target_spec: unknown;
  split_spec: unknown;
  params: unknown;
  seed: number;
  calibration: string;
  max_seconds: number;
  autotune?: unknown;
}

export interface CreateVersionResult {
  version_no: number;
  job_id: string;
}

/** One §10.5 progress frame, as the SSE `progress` event carries it. */
export interface TrainingProgress {
  stage?: string;
  pct?: number | null;
  round?: number | null;
  epoch?: number | null;
  total?: number | null;
  train_loss?: number | null;
  val_loss?: number | null;
  val_metric?: number | null;
  metric?: string | null;
  eta_s?: number | null;
  t?: number | null;
  queue_position?: number | null;
}

export interface ActivateResult {
  updated: { strategy_id: string; version_no: number; name?: string }[];
  skipped: {
    strategy_id: string;
    reason: "live_run_active" | "hash_mismatch" | "conflict";
    name?: string;
  }[];
}

export interface PredictPreviewRow {
  t: number;
  inputs_summary: Record<string, unknown>;
  value: string | null;
  prob: string | null;
  class: number | null;
  error?: { class: string; message: string } | null;
}

// ---------------------------------------------------------------------------
// §11.4 assistant patch proposals
// ---------------------------------------------------------------------------

/** One row of the block-level diff the proposal card renders. */
export interface PatchDiffRow {
  change: "added" | "removed" | "changed";
  block_id: string;
  block_kind: string;
  path: string;
  before?: string;
  after?: string;
}

/** One line of the red "Changes order behaviour" banner. Computed server-side. */
export interface TradeDelta {
  kind: string;
  block_id: string | null;
  detail: string;
}

export type PatchProposalStatus =
  | "proposed"
  | "accepted"
  | "rejected"
  | "expired"
  | "superseded";

/** `GET /strategies/:id/patches` and the SSE `patch_proposal` frame (§11.4). */
export interface PatchProposal {
  id: string;
  strategy_id: string;
  thread_id: string | null;
  status: PatchProposalStatus;
  rationale: string;
  summary: string;
  base_ast_hash: string;
  result_ast_hash: string;
  patch: Array<{ op: string; path: string; from?: string; value?: unknown }>;
  diff: PatchDiffRow[];
  trade_affecting: boolean;
  trade_delta: TradeDelta[];
  diagnostics: Diagnostic[];
  expires_at: string;
  created_at: string;
}

export interface AcceptPatchResult {
  proposal_id: string;
  status: "accepted";
  strategy_id: string;
  ast_hash: string;
  /** The regenerated Blockly document; untouched stacks keep their layout. */
  workspace: unknown;
  version_no: number;
  document_version: number;
  diagnostics: Diagnostic[];
  trade_affecting: boolean;
}

/** §11.2's focus hint, sent with every assistant message. */
export interface ChatFocus {
  tab: string;
  selected_block_id?: string;
  model_id?: string;
  api_id?: string;
}

export interface ChatMessageInput {
  message: string;
  focus: ChatFocus;
}

/** One answered onboarding step, as `PUT /me/onboarding` takes it. */
export interface OnboardingStepInput {
  state?: Exclude<OnboardingState, "done">;
  preferred_venue?: VenueT;
  execution_locality?: "hosted" | "self_hosted";
}

/**
 * `GET /auth/config` — what the sign-up and sign-in screens need to know
 * before anybody is signed in (track S1). No session; safe to read cold.
 */
export interface AuthConfig {
  signup: {
    /** `open`: anybody. `invite`: a code is required. `closed`: nobody. */
    mode: "open" | "invite" | "closed";
    /** The request header the code travels in. */
    invite_header: string;
  };
  password: { min_length: number; max_length: number };
  /** False when the deployment has no mail provider: there is no reset link to send. */
  password_reset: { available: boolean };
  email_verification: {
    enforced: boolean;
    required_for: ("store_venue_credential" | "pair_agent" | "start_live_run")[];
  };
}

/** `GET /me/email-verification`. */
export interface EmailVerificationStatus {
  email: string;
  verified: boolean;
  /**
   * Whether THIS deployment could mail a confirmation link right now. False
   * with no mail provider configured; the browser shows no prompt then.
   */
  can_send: boolean;
}

/** `GET /me/jurisdiction` — the attestation on file, and what it decided. */
export interface JurisdictionRecord {
  country: string | null;
  region: string | null;
  is_us_person: boolean;
  polymarket_live_allowed: boolean;
  signup_ip_country: string | null;
  attestation: { attested_at?: string | null } | null;
}

/**
 * `DELETE /me` → the receipt (track S2). The session is GONE once this
 * arrives, so it is rendered from this body on a signed-out page; `pending`
 * and `not_reached` are written for the user and shown as they are.
 */
export interface AccountDeletionReceipt {
  status: "scheduled" | string;
  requested_at: string;
  purge_after: string;
  grace_days: number;
  done?: {
    runs_stopped?: number;
    agents_revoked?: number;
    sessions_revoked?: number;
    credentials?: "destroyed" | "deferred" | "none";
    data_keys_destroyed?: number;
    venue_credentials_destroyed?: number;
    api_secrets_destroyed?: number;
  };
  pending?: { what: string; detail: string }[];
  not_reached?: { what: string; detail: string }[];
  self_hosted_runs?: unknown[];
  email_confirmation?: "sent" | "not_sent" | "not_configured";
}

/** Who engaged a switch, as `GET /kill-switch` says it (backend B2). */
export type KillSwitchEngagedBy = "you" | "operator" | "madaline";

/** One engaged row of `GET /kill-switch`. */
export interface KillSwitchRow {
  scope: string;
  engaged_at: string;
  reason: string | null;
  released_at: string | null;
  venue_ack_at: string | null;
  cancelled_orders: number | null;
  /** `user:<id>`, `admin:cli:<operator>`, or another system actor. */
  engaged_by?: string | null;
  engaged_by_kind?: KillSwitchEngagedBy;
  /** What engaging this scope refuses. */
  blocks?: "everyone" | "venue" | "account" | "run";
  /** Only a switch this person engaged; never an operator's hold. */
  can_release?: boolean;
}

/** A run the account's hold stopped (`account.stopped_runs`). */
export interface KillSwitchStoppedRun {
  id: string;
  strategy_id: string;
  strategy_name: string;
  strategy_deleted: boolean;
  mode: string;
  venue: string;
  actual_state: string;
  stop_reason: string | null;
  stopped_at: string | null;
}

/** What holds THIS account (`scope user:<me>`), when something does. */
export interface KillSwitchAccountHold {
  scope: string;
  engaged_at: string;
  engaged_by: string;
  engaged_by_kind: KillSwitchEngagedBy;
  reason: string | null;
  stopped_runs: KillSwitchStoppedRun[];
  cancelled_orders: number | null;
  venue_ack_at: string | null;
  /** True only while a stopped LIVE run's cancel at the venue is unconfirmed. */
  pending_venue_confirmation: boolean;
  /** False for an operator's hold: this person cannot release it. */
  can_release: boolean;
  /** The server's one sentence about the hold, shown as written. */
  message: string;
}

/** `GET /kill-switch` as backend B2 shipped it. The fields after `engaged` are B2's. */
export interface KillSwitchState {
  killed: boolean;
  engaged: KillSwitchRow[];
  /** This account's own scope or a global switch is engaged: every new run is refused. */
  blocks_new_runs?: boolean;
  /** Venues an operator has switched off, e.g. `["kalshi"]`. */
  blocked_venues?: string[];
  account?: KillSwitchAccountHold | null;
}

/** `POST /runs/stop-all` — every run of this account stopped as Stop stops one; nothing held. */
export interface StopAllResult {
  stopped: Array<{ id: string; strategy_id: string; strategy_name: string; mode: string; state: string }>;
  already_stopping: string[];
}

// ---------------------------------------------------------------------------
// The way in, the account and its record (track I1)
// ---------------------------------------------------------------------------

/**
 * `GET /auth/session` — who is signed in in this browser, and how far. 200
 * for everybody, so a page a link lands on can ask without the browser
 * logging a failed request for "nobody".
 */
export type SessionProbe =
  | { state: "signed_out"; second_factor_expired?: boolean }
  | { state: "second_factor"; second_factor: SignInChallenge }
  | {
      state: "signed_in";
      email_verified: boolean;
      two_factor_enrolled: boolean;
      /** When the last challenge stops counting for step-up; null when it does not. */
      fresh_until: string | null;
    };

/** `POST /auth/sign-in/second-factor` — the sign-in is complete. */
export interface SignInAnswer {
  signed_in: true;
  factor: "totp" | "recovery_code";
  recovery_codes_remaining: number;
  fresh_until: string;
}

/** `PATCH /me/profile`. */
export interface ProfilePatch {
  display_name?: string;
  timezone?: string;
  execution_locality_default?: "hosted" | "self_hosted";
}

export interface ProfileFields {
  display_name: string | null;
  timezone: string;
  execution_locality_default: "hosted" | "self_hosted";
}

/** One document of the corpus as `GET /legal/:slug` serves it. */
export interface ServedLegalDocument {
  doc_key: string;
  slug: string;
  title: string;
  version: string;
  effective_date: string;
  summary: string;
  acceptance_required: boolean;
  /** Whether counsel has reviewed this version. */
  reviewed: boolean;
  /** Which shelf of `/legal` it sits on. Absent on an API older than 2026-10-15. */
  group?: "agreement" | "policy" | "disclosure" | "statement";
  /** The rendered text: the operator's details in place, or marked "not yet filled in". */
  body: string;
  /** Of `body`, exactly as served. */
  sha256: string;
}

export type LegalManifestEntry = Omit<ServedLegalDocument, "body" | "sha256">;

/** `GET /legal`: the four disclosures, the two agreements, and the rest. */
export interface LegalManifest {
  documents: LegalManifestEntry[];
  /** Absent on an API older than track I1. */
  agreements?: LegalManifestEntry[];
  /** The policies the Terms incorporate, and the statements. Absent before 2026-10-15. */
  references?: LegalManifestEntry[];
  /** The operator's details not filled in yet (`packages/shared/src/legal/operator.ts`). */
  operator?: { unset: Array<{ token: string; label: string; decided_by: "owner" | "counsel" }> };
}

/** `GET /me/agreements` — the Terms and the Privacy policy on file for this account. */
export interface AgreementStatusRow {
  doc_key: "terms_of_service" | "privacy_policy";
  slug: string;
  title: string;
  version: string;
  effective_date: string;
  reviewed: boolean;
  accepted: boolean;
  accepted_version: string | null;
  accepted_at: string | null;
}

/** `GET /billing` — only on a deployment where billing is configured. */
export interface BillingState {
  plan: string;
  plan_name: string;
  plan_source: "default" | "billing" | "operator";
  subscription: null | {
    /** Stripe's own word: active, trialing, past_due, unpaid, canceled, … */
    status: string;
    plan: string | null;
    plan_name: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    payment_failed_at: string | null;
    next_payment_attempt: string | null;
  };
  can_subscribe: boolean;
  can_manage: boolean;
  last_plan_change: null | {
    at: string;
    from: string;
    to: string;
    cause: "webhook" | "operator";
    subscription_status: string | null;
    runs_stopped: Array<{ id: string; mode: string; why: string }>;
  };
}

/** One plan of `GET /billing/plans`: what it includes and what it costs. */
export interface BillingPlan {
  plan: string;
  display_name: string;
  current: boolean;
  for_sale: boolean;
  price_problem?: "not_found" | "inactive" | "not_recurring" | "not_monthly" | "wrong_mode" | "unavailable";
  price: null | {
    amount_minor: number | null;
    /** Decimal string; never through a float. */
    amount: string | null;
    currency: string;
    interval: string;
    interval_count: number;
  };
  limits: PlanValue["limits"];
  allowed: PlanValue["allowed"];
}

/** `POST /kill-switch` — what "Stop everything" did, and what it could not reach. */
export interface KillSwitchEngaged {
  scope: string | null;
  scopes: string[];
  stopped_runs: string[];
  cancelled_orders: number;
  venue_confirmed: boolean;
  pending_venue_confirmation: boolean;
  venue_unreachable_runs: string[];
  venue_error?: string;
}

export interface MadalineApi {
  // auth
  /**
   * The password half. Resolves with nothing when that signed the browser
   * in, and with the challenge when the account has a second factor: the
   * browser then holds a sign-in that can only answer it (KNOWN-DEBT D5).
   */
  signIn(credentials: PasswordCredentials): Promise<SignInChallenge | void>;
  signUp(input: SignUpInput): Promise<void>;
  /** `GET /auth/session` — never a 401: "nobody" is an answer. */
  getAuthSession(): Promise<SessionProbe>;
  /** `POST /auth/sign-in/second-factor` — a six-digit code, or a recovery code. */
  answerSignInChallenge(code: string): Promise<SignInAnswer>;
  /** `PATCH /me/profile` — validated by the server. */
  updateProfile(patch: ProfilePatch): Promise<ProfileFields>;
  /** `GET /legal` — the manifest, with each document's review state. */
  getLegalManifest(): Promise<LegalManifest>;
  /** `GET /legal/:slug` — one document, as the server would record an acceptance of it. */
  getLegalDocument(slug: string): Promise<ServedLegalDocument>;
  /** `GET /me/agreements`. */
  getAgreements(): Promise<{ documents: AgreementStatusRow[] }>;
  /** `POST /me/agreements {doc_key, version}`. */
  acceptAgreement(input: { doc_key: string; version: string }): Promise<void>;
  /** `POST /runs/stop-all` — no step-up, and nothing held afterwards. */
  stopAllRuns(input: { cancel_open_orders: boolean }): Promise<StopAllResult>;
  /** `GET /billing` — 404 when billing is off on this deployment. */
  getBilling(): Promise<BillingState>;
  /** `GET /billing/plans` — 404 when billing is off. */
  getBillingPlans(): Promise<{ plans: BillingPlan[] }>;
  /** `POST /billing/checkout {plan}` → where to send the browser to pay. */
  startCheckout(plan: string): Promise<{ url: string; session_id: string }>;
  /** `POST /billing/portal` → the payment provider's page for this account. */
  openBillingPortal(): Promise<{ url: string }>;
  /** Signs THIS browser out. */
  signOut(): Promise<void>;
  /** Ends every session of the account, this one included. */
  signOutEverywhere(): Promise<void>;
  /** Every signed-in session, and which one is this browser. */
  listSessions(): Promise<{ current: string | null; sessions: AuthSession[] }>;
  /** Signs one other session out. */
  revokeSession(token: string): Promise<void>;
  /** Signs every other session out, keeping this one. */
  revokeOtherSessions(): Promise<void>;
  /** Ends every other session; this browser gets a new cookie. */
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<void>;
  /** `GET /me/jurisdiction`. */
  getJurisdiction(): Promise<JurisdictionRecord>;
  /**
   * `POST /me/export` — step-up, three an hour. The account's own work as one
   * JSON document, for the user's records. There is no import.
   */
  exportAccount(): Promise<{ blob: Blob; filename: string }>;
  /** `DELETE /me` — step-up, and the account's own address typed back. */
  deleteAccount(confirmEmail: string): Promise<AccountDeletionReceipt>;
  /** `GET /kill-switch` — whether Stop everything is holding this account. */
  getKillSwitch(): Promise<KillSwitchState>;
  /** `POST /kill-switch` — step-up. Stops every run and refuses new ones until released. */
  engageKillSwitch(input?: { cancel_open_orders?: boolean }): Promise<KillSwitchEngaged>;
  /** `DELETE /kill-switch` — step-up. Runs may start again. */
  releaseKillSwitch(): Promise<{ released: number; scopes: string[] }>;
  // profile
  getMe(): Promise<Me>;
  completeOnboarding(input: OnboardingInput): Promise<{ first_strategy_id: string }>;
  /**
   * `PUT /me/onboarding` — record the answer to one onboarding step. The
   * server's state only moves forward, and it refuses to step past the
   * disclosures while one is outstanding (`422 DISCLOSURES_REQUIRED`).
   */
  saveOnboardingStep(input: OnboardingStepInput): Promise<void>;
  /** `GET /me/email-verification` — confirmed? and could a link be sent at all? */
  getEmailVerification(): Promise<EmailVerificationStatus>;
  /** `POST /me/email-verification` — send another confirmation link. */
  resendEmailVerification(): Promise<{ status: "sent" | "already_verified"; email: string }>;
  /**
   * `POST /auth/forgot-password`. The answer is the same whether or not the
   * address has an account — the server's message is shown as it is written.
   */
  requestPasswordReset(email: string): Promise<{ message: string }>;
  /** `POST /auth/reset-password` — spend the emailed token on a new password. */
  resetPassword(input: { token: string; new_password: string }): Promise<{ message: string }>;
  /** `GET /auth/config` — the sign-up mode, the password rule, whether a reset can be mailed. */
  getAuthConfig(): Promise<AuthConfig>;
  // strategies
  listStrategies(): Promise<StrategySummary[]>;
  createStrategy(input: CreateStrategyInput): Promise<StrategyDocument>;
  getStrategy(id: string): Promise<StrategyDocument>;
  saveStrategy(id: string, input: SaveStrategyInput): Promise<SaveResult>;
  renameStrategy(id: string, name: string): Promise<StrategyDocument>;
  deleteStrategy(id: string): Promise<void>;
  validateStrategy(
    id: string,
    ast: unknown,
  ): Promise<{ ok: boolean; diagnostics: Diagnostic[]; ast_hash: string }>;
  // runs
  startRun(strategyId: string, input: StartRunInput): Promise<{ run_id: string }>;
  stopRun(runId: string, input: { cancel_open_orders: boolean }): Promise<{ state: string }>;
  getRun(runId: string): Promise<RunSummary>;
  listRuns(query?: { strategy_id?: string; state?: string; limit?: number }): Promise<{
    runs: RunSummary[];
  }>;
  runHistory(runId: string, query?: { after?: string; limit?: number }): Promise<RunHistoryPage>;
  /** The URL the SSE client opens for a run console. */
  runEventsUrl(runId: string): string;
  /**
   * Open the run's event stream. It hangs off the API façade rather than being
   * called directly so a test drives the console without a network, and so the
   * concurrent-stream cap is enforced in one place.
   */
  openRunStream(runId: string, options: RunStreamOptions): SseHandle;
  /**
   * §13 `PUT /me/jurisdiction`. Not implemented by the Phase 2 API; a 404 is
   * swallowed so signup still completes, and the answer the user gave is
   * re-sent by the onboarding region step once the route exists.
   */
  setJurisdiction(input: {
    country: string;
    region?: string;
    is_us_person: boolean;
    attested: true;
  }): Promise<void>;

  // ---------------------------------------------------------------- settings

  /** §13 `GET /me/plan` — the tier, its limits and what has been used. */
  getPlan(): Promise<PlanValue>;
  /** §13 `GET /me/2fa`. */
  getTwoFactor(): Promise<TwoFactorStatus>;
  /**
   * `POST /me/2fa/enroll` — mints a secret and returns the provisioning URI,
   * the QR payload and the recovery codes ONCE. The account is not enrolled
   * until `verifyTwoFactor` succeeds against it.
   */
  enrollTwoFactor(password: string): Promise<TwoFactorEnrolment>;
  /**
   * `POST /me/2fa/verify` — a six-digit code or one recovery code. This is
   * what makes the session fresh for the step-up routes.
   */
  verifyTwoFactor(code: string): Promise<TwoFactorChallengeResult>;
  /** `POST /me/2fa/recovery-codes` — new codes, retiring every old one. */
  regenerateRecoveryCodes(password: string): Promise<{ recovery_codes: string[] }>;
  /** `POST /me/2fa/disable` — needs a session that just passed the challenge. */
  disableTwoFactor(): Promise<{ enrolled: false }>;
  /**
   * `POST /me/2fa/dev-enable` — the development-only step-up bypass. Call it
   * ONLY when `getMe()` reported `dev_step_up_available: true`; on any other
   * server the route does not exist and this is a 404.
   */
  devEnableStepUp(): Promise<DevStepUpResult>;
  /** §13 `GET /me/risk-defaults`. */
  getRiskDefaults(): Promise<RiskDefaults>;
  /**
   * §13 `PUT /me/risk-defaults` — a step-up route. It can answer
   * `409 RUN_ACTIVE_RESTART_REQUIRED` for a loosening while a run is live and
   * `401 STEP_UP_REQUIRED` when the session is not fresh; both reach the
   * caller as an `ApiError` so the screen can say which happened.
   */
  updateRiskDefaults(patch: RiskLimitsPatch): Promise<RiskDefaults>;
  /** §13 `GET /credentials` — metadata only; key material never leaves the API. */
  listCredentials(): Promise<CredentialCardValue[]>;
  /** §13 `DELETE /credentials/:id`. */
  deleteCredential(id: string): Promise<void>;
  /** §12.5 `POST /credentials/:id/acknowledge-transfer`. */
  acknowledgeCredentialTransfer(id: string): Promise<CredentialCardValue>;
  /**
   * §13 `POST /credentials/kalshi` — validated at the venue, then sealed. A
   * step-up route; the PEM is in this request and in no answer.
   */
  connectKalshiKey(input: ConnectKalshiKeyInput): Promise<ConnectedKalshiKey>;
  /** §13 `GET /secrets` — names, never values. */
  listSecrets(): Promise<ApiSecretList>;
  /** §13 `POST /secrets` (step-up) — set or replace one value by name. */
  saveSecret(input: { name: string; value: string; kind?: ApiSecret["kind"] }): Promise<SaveSecretResult>;
  /** §13 `DELETE /secrets/:id` (step-up); `409 SECRET_IN_USE` while an API signs in with it. */
  deleteSecret(id: string): Promise<void>;
  /** §13 `GET /agents` — paired computers, metadata only. */
  listAgents(): Promise<PairedAgent[]>;
  /** §13 `POST /agents/pairing-codes`; the plaintext exists only in the reply. */
  createPairingCode(): Promise<PairingCode>;
  /** §13 `POST /agents/:id/revoke` — stops the runs it holds first. */
  revokeAgent(id: string): Promise<void>;
  // ------------------------------------------------------------ custom APIs

  /** §13 `GET /apis` — global definitions, plus this strategy's own. */
  listApis(query?: { strategy_id?: string }): Promise<ApiDefinitionSummary[]>;
  /** §13 `GET /apis/:id`. */
  getApi(id: string): Promise<ApiDefinitionDetail>;
  /** §13 `POST /apis` — from scratch, or from a §9.1 built-in template. */
  createApi(input: CreateApiInput): Promise<ApiDefinitionDetail>;
  /** §13 `PATCH /apis/:id`. Every edit bumps `version` (§9.3). */
  updateApi(id: string, patch: UpdateApiInput): Promise<ApiDefinitionDetail>;
  /** §13 `DELETE /apis/:id` — soft delete; pinned blocks stay explicable. */
  deleteApi(id: string): Promise<void>;
  /**
   * §9.3 step 2 — the request is made by the SERVER through the hardened
   * fetcher. The browser never calls a user's endpoint.
   */
  testApi(
    id: string,
    input: { params?: Record<string, string | number | boolean>; confirm_mutating?: boolean },
  ): Promise<ApiTestResult>;
  /**
   * §9.3 step 3 — one path against the stored sample, evaluated by the same
   * function the strategy runtime uses. This is the only way a preview is
   * produced; there is no client-side JSONPath evaluator.
   */
  previewApiField(
    id: string,
    input: {
      path: string;
      name?: string;
      type?: ApiFieldTypeT;
      multi?: boolean;
      transforms?: unknown[];
    },
  ): Promise<ApiPathPreview>;
  /** §14.5 "Promote to global". */
  promoteApi(id: string): Promise<ApiDefinitionDetail>;
  /** §13 `GET /apis/:id/used-by` — which strategies pin it, and how stale. */
  apiUsedBy(id: string): Promise<ApiUsedBy[]>;

  /** §13 `GET /disclosures` — the same status the run gate reads. */
  // datasets & models (§13 "Datasets & models")
  venueCoverage(query?: {
    venue?: string;
    series?: string;
    kind?: string;
  }): Promise<VenueCoverageResponse>;
  listDatasets(query?: { strategy_id?: string }): Promise<DatasetSummary[]>;
  createDataset(input: CreateDatasetInput): Promise<CreateDatasetResult>;
  getDataset(id: string): Promise<DatasetSummary>;
  datasetCoverage(id: string): Promise<DatasetCoverageResponse>;
  datasetPreview(
    id: string,
    limit?: number,
  ): Promise<{ rows: Record<string, unknown>[]; count: number }>;

  listModels(query?: { strategy_id?: string }): Promise<ModelList>;
  createModel(input: CreateModelInput): Promise<ModelSummary>;
  getModel(id: string): Promise<ModelSummary>;
  deleteModel(id: string): Promise<void>;
  saveModelDraft(id: string, spec: unknown): Promise<{ ok: true }>;
  createModelVersion(id: string, input: CreateVersionInput): Promise<CreateVersionResult>;
  getModelVersion(id: string, version: number): Promise<ModelVersionDetail>;
  cancelModelVersion(id: string, version: number): Promise<{ state: string }>;
  activateModelVersion(
    id: string,
    version: number,
    input: { rebind_same_hash: boolean },
  ): Promise<ActivateResult>;
  deprecateModelVersion(id: string, version: number): Promise<{ status: ModelVersionStatus }>;
  promoteModel(id: string): Promise<ModelSummary>;
  modelTestPredictions(id: string, version: number): Promise<{ predictions: TestPrediction[] }>;
  predictPreview(id: string, version: number, n: number): Promise<{ rows: PredictPreviewRow[] }>;
  /** §13: `GET /models/:id/versions/:v/events` — `progress|log|done|failed|cancelled`. */
  openTrainingStream(id: string, version: number, options: RunStreamOptions): SseHandle;

  /** §11.4 — the assistant's proposals for one strategy, newest first. */
  listPatchProposals(strategyId: string): Promise<{ proposals: PatchProposal[] }>;
  acceptPatch(
    proposalId: string,
    input: { base_ast_hash: string; ack_trade_change?: boolean },
  ): Promise<AcceptPatchResult>;
  rejectPatch(proposalId: string): Promise<{ proposal_id: string; status: "rejected" }>;
  /** §11.1 — the POST chat stream. Never reconnects (§14.7). */
  openChatStream(
    strategyId: string,
    options: RunStreamOptions & { body: ChatMessageInput },
  ): SseHandle;

  getDisclosures(): Promise<DisclosureStatus>;
  /** §13 `POST /me/disclosures {doc_key, version}`. */
  acceptDisclosure(input: { doc_key: string; version: string }): Promise<void>;
}

export type RunStreamOptions = Pick<
  OpenSseOptions,
  "lastEventId" | "onMessage" | "onStatus" | "onError"
>;

export interface CreateMadalineApiOptions {
  client?: ApiClient;
}

export function createMadalineApi(options: CreateMadalineApiOptions = {}): MadalineApi {
  const client = options.client ?? createApiClient();

  return {
    signIn: (credentials) => signInWithPassword(credentials),
    signUp: (input) => signUpWithPassword(input),
    getAuthSession: () => client.get<SessionProbe>("/auth/session"),
    answerSignInChallenge: (code) => client.post<SignInAnswer>("/auth/sign-in/second-factor", { code }),
    updateProfile: (patch) => client.patch<ProfileFields>("/me/profile", patch),
    getLegalManifest: () => client.get<LegalManifest>("/legal"),
    getLegalDocument: (slug) => client.get<ServedLegalDocument>(`/legal/${encodeURIComponent(slug)}`),
    getAgreements: () => client.get<{ documents: AgreementStatusRow[] }>("/me/agreements"),
    async acceptAgreement(input) {
      await client.post("/me/agreements", input);
    },
    stopAllRuns: (input) => client.post<StopAllResult>("/runs/stop-all", input),
    getBilling: () => client.get<BillingState>("/billing"),
    getBillingPlans: () => client.get<{ plans: BillingPlan[] }>("/billing/plans"),
    startCheckout: (plan) => client.post<{ url: string; session_id: string }>("/billing/checkout", { plan }),
    openBillingPortal: () => client.post<{ url: string }>("/billing/portal"),
    signOut: () => signOutHere(),
    signOutEverywhere: () => signOutEverywhere(),
    listSessions: () => listAuthSessions(),
    revokeSession: (token) => revokeAuthSession(token),
    revokeOtherSessions: () => revokeOtherAuthSessions(),
    changePassword: (input) => changeAccountPassword(input),
    getJurisdiction: () => client.get<JurisdictionRecord>("/me/jurisdiction"),
    async exportAccount() {
      // The server answers with the document itself; it is turned back into
      // a file here so the browser saves it rather than a tab rendering it.
      const doc = await client.post<unknown>("/me/export");
      const day = new Date().toISOString().slice(0, 10);
      return {
        blob: new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" }),
        filename: `madaline-export-${day}.json`,
      };
    },
    deleteAccount: (confirmEmail) =>
      client.del<AccountDeletionReceipt>("/me", { body: { confirm_email: confirmEmail } }),
    getKillSwitch: () => client.get<KillSwitchState>("/kill-switch"),
    engageKillSwitch: (input) =>
      client.post<KillSwitchEngaged>("/kill-switch", {
        scope: "user",
        cancel_open_orders: input?.cancel_open_orders ?? true,
      }),
    releaseKillSwitch: () => client.del<{ released: number; scopes: string[] }>("/kill-switch"),

    getMe: () => client.get<Me>("/me"),

    /**
     * §13 defines `POST /onboarding/complete {venue, template}` ->
     * `{first_strategy_id}`. Phase 2's API does not implement it yet (Task 2
     * shipped the strategies routes only), so a 404 falls back to the create
     * call it is sugar for. The fallback disappears on its own the day the
     * route lands — nothing here needs changing.
     */
    async completeOnboarding(input) {
      try {
        return await client.post<{ first_strategy_id: string }>("/onboarding/complete", input);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
        const strategy = await client.post<StrategyDocument>("/strategies", {
          venue: input.venue,
          template: input.template,
        });
        return { first_strategy_id: strategy.id };
      }
    },

    /**
     * A 404 means an API older than the route; nothing is recorded there and
     * the flow carries on exactly as it did before the route existed. Every
     * other failure — notably `422 DISCLOSURES_REQUIRED` — is the caller's to
     * show.
     */
    async saveOnboardingStep(input) {
      try {
        await client.put("/me/onboarding", input);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
      }
    },
    getEmailVerification: () => client.get<EmailVerificationStatus>("/me/email-verification"),
    resendEmailVerification: () =>
      client.post<{ status: "sent" | "already_verified"; email: string }>("/me/email-verification"),
    requestPasswordReset: (email) =>
      client.post<{ message: string }>("/auth/forgot-password", { email }),
    resetPassword: (input) => client.post<{ message: string }>("/auth/reset-password", input),
    getAuthConfig: () => client.get<AuthConfig>("/auth/config"),

    listStrategies: () => client.get<StrategySummary[]>("/strategies"),
    createStrategy: (input) => client.post<StrategyDocument>("/strategies", input),
    getStrategy: (id) => client.get<StrategyDocument>(`/strategies/${id}`),
    saveStrategy: (id, input) => client.put<SaveResult>(`/strategies/${id}`, input),
    renameStrategy: (id, name) => client.patch<StrategyDocument>(`/strategies/${id}`, { name }),
    deleteStrategy: (id) => client.del<void>(`/strategies/${id}`),
    validateStrategy: (id, ast) =>
      client.post<{ ok: boolean; diagnostics: Diagnostic[]; ast_hash: string }>(
        `/strategies/${id}/validate`,
        { ast },
      ),

    startRun: (strategyId, input) =>
      client.post<{ run_id: string }>(`/strategies/${strategyId}/runs`, input),
    stopRun: (runId, input) => client.post<{ state: string }>(`/runs/${runId}/stop`, input),
    getRun: (runId) => client.get<RunSummary>(`/runs/${runId}`),
    listRuns: (query) => client.get<{ runs: RunSummary[] }>("/runs", { query }),
    runHistory: (runId, query) =>
      client.get<RunHistoryPage>(`/runs/${runId}/events/history`, { query }),
    runEventsUrl: (runId) => client.url(`/runs/${runId}/events`),
    openRunStream: (runId, streamOptions) =>
      openSse({ url: client.url(`/runs/${runId}/events`), kind: "run", ...streamOptions }),

    getPlan: () => client.get<PlanValue>("/me/plan"),
    getTwoFactor: () => client.get<TwoFactorStatus>("/me/2fa"),
    enrollTwoFactor: (password) =>
      client.post<TwoFactorEnrolment>("/me/2fa/enroll", { password }),
    verifyTwoFactor: (code) =>
      client.post<TwoFactorChallengeResult>("/me/2fa/verify", { code }),
    regenerateRecoveryCodes: (password) =>
      client.post<{ recovery_codes: string[] }>("/me/2fa/recovery-codes", { password }),
    disableTwoFactor: () => client.post<{ enrolled: false }>("/me/2fa/disable"),
    devEnableStepUp: () => client.post<DevStepUpResult>("/me/2fa/dev-enable"),

    getRiskDefaults: () => client.get<RiskDefaults>("/me/risk-defaults"),
    updateRiskDefaults: (patch) => client.put<RiskDefaults>("/me/risk-defaults", patch),

    async listCredentials() {
      const body = await client.get<{ credentials: CredentialCardValue[] }>("/credentials");
      return body.credentials;
    },
    async deleteCredential(id) {
      await client.del<void>(`/credentials/${id}`);
    },
    acknowledgeCredentialTransfer: (id) =>
      client.post<CredentialCardValue>(`/credentials/${id}/acknowledge-transfer`),
    connectKalshiKey: (input) => client.post<ConnectedKalshiKey>("/credentials/kalshi", input),
    listSecrets: () => client.get<ApiSecretList>("/secrets"),
    saveSecret: (input) => client.post<SaveSecretResult>("/secrets", input),
    async deleteSecret(id) {
      await client.del<void>(`/secrets/${id}`);
    },

    async listAgents() {
      const body = await client.get<{ agents: PairedAgent[] }>("/agents");
      return body.agents;
    },
    createPairingCode: () => client.post<PairingCode>("/agents/pairing-codes"),
    async revokeAgent(id) {
      await client.post(`/agents/${id}/revoke`);
    },

    async listApis(query) {
      const body = await client.get<{ apis: ApiDefinitionSummary[] }>("/apis", {
        ...(query?.strategy_id ? { query: { strategy_id: query.strategy_id } } : {}),
      });
      return body.apis;
    },
    getApi: (id) => client.get<ApiDefinitionDetail>(`/apis/${id}`),
    createApi: (input) => client.post<ApiDefinitionDetail>("/apis", input),
    updateApi: (id, patch) => client.patch<ApiDefinitionDetail>(`/apis/${id}`, patch),
    async deleteApi(id) {
      await client.del<void>(`/apis/${id}`);
    },
    testApi: (id, input) => client.post<ApiTestResult>(`/apis/${id}/test`, input),
    previewApiField: (id, input) =>
      client.post<ApiPathPreview>(`/apis/${id}/preview-field`, input),
    promoteApi: (id) => client.post<ApiDefinitionDetail>(`/apis/${id}/promote`),
    async apiUsedBy(id) {
      const body = await client.get<{ strategies: ApiUsedBy[] }>(`/apis/${id}/used-by`);
      return body.strategies;
    },

    // -- datasets & models --------------------------------------------------

    venueCoverage: (query) =>
      client.get<VenueCoverageResponse>("/datasets/coverage", {
        ...(query && Object.keys(query).length > 0 ? { query } : {}),
      }),
    async listDatasets(query) {
      const body = await client.get<{ datasets: DatasetSummary[] }>("/datasets", {
        ...(query?.strategy_id ? { query: { strategy_id: query.strategy_id } } : {}),
      });
      return body.datasets;
    },
    createDataset: (input) => client.post<CreateDatasetResult>("/datasets", input),
    getDataset: (id) => client.get<DatasetSummary>(`/datasets/${id}`),
    datasetCoverage: (id) => client.get<DatasetCoverageResponse>(`/datasets/${id}/coverage`),
    datasetPreview: (id, limit) =>
      client.get<{ rows: Record<string, unknown>[]; count: number }>(`/datasets/${id}/preview`, {
        ...(limit === undefined ? {} : { query: { limit } }),
      }),

    listModels: (query) =>
      client.get<ModelList>("/models", {
        ...(query?.strategy_id ? { query: { strategy_id: query.strategy_id } } : {}),
      }),
    createModel: (input) => client.post<ModelSummary>("/models", input),
    getModel: (id) => client.get<ModelSummary>(`/models/${id}`),
    async deleteModel(id) {
      await client.del<void>(`/models/${id}`);
    },
    saveModelDraft: (id, spec) => client.put<{ ok: true }>(`/models/${id}/draft`, { spec }),
    createModelVersion: (id, input) =>
      client.post<CreateVersionResult>(`/models/${id}/versions`, input),
    getModelVersion: (id, version) =>
      client.get<ModelVersionDetail>(`/models/${id}/versions/${version}`),
    cancelModelVersion: (id, version) =>
      client.post<{ state: string }>(`/models/${id}/versions/${version}/cancel`),
    activateModelVersion: (id, version, input) =>
      client.post<ActivateResult>(`/models/${id}/versions/${version}/activate`, input),
    deprecateModelVersion: (id, version) =>
      client.post<{ status: ModelVersionStatus }>(`/models/${id}/versions/${version}/deprecate`),
    promoteModel: (id) => client.post<ModelSummary>(`/models/${id}/promote`),
    /**
     * §10.5: the API never reads the volume — it proxies `test_predictions_url`.
     * The client asks for the ROUTE rather than the URL the detail carried, so a
     * response naming someone else's path could not redirect this fetch.
     */
    modelTestPredictions: (id, version) =>
      client.get<{ predictions: TestPrediction[] }>(
        `/models/${id}/versions/${version}/test-predictions`,
      ),
    predictPreview: (id, version, n) =>
      client.post<{ rows: PredictPreviewRow[] }>(
        `/models/${id}/versions/${version}/predict-preview`,
        { n },
      ),
    openTrainingStream: (id, version, streamOptions) =>
      openSse({
        url: client.url(`/models/${id}/versions/${version}/events`),
        kind: "training",
        terminalEvents: ["done", "failed", "cancelled"],
        ...streamOptions,
      }),

    listPatchProposals: (strategyId) =>
      client.get<{ proposals: PatchProposal[] }>(`/strategies/${strategyId}/patches`),
    acceptPatch: (proposalId, input) =>
      client.post<AcceptPatchResult>(`/patches/${proposalId}/accept`, input),
    rejectPatch: (proposalId) =>
      client.post<{ proposal_id: string; status: "rejected" }>(`/patches/${proposalId}/reject`),
    openChatStream: (strategyId, { body, ...streamOptions }) =>
      openSse({
        url: client.url(`/chat/${strategyId}/messages`),
        method: "POST",
        body,
        kind: "chat",
        // §11.1's frames. `refused` is terminal too: the pipeline answers it
        // instead of a stream, and there is nothing after it.
        terminalEvents: ["done", "error", "refused"],
        reconnectOnClose: false,
        ...streamOptions,
      }),

    getDisclosures: () => client.get<DisclosureStatus>("/disclosures"),
    async acceptDisclosure(input) {
      await client.post("/me/disclosures", input);
    },

    async setJurisdiction(input) {
      try {
        await client.put("/me/jurisdiction", input);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
      }
    },
  };
}
